import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { getErrorMessage } from "@/utilities";
import {
  createOfflineMethod,
  deleteOfflineMethod,
  listOfflineMethods,
  updateOfflineMethod,
  uploadOfflineIcon,
} from "./api";
import { OfflineOption, OfflinePaymentMethod } from "./types";
import {
  CuerpoConPie,
  ENCABEZADO_DE_TABLA,
  PAGINADOR,
  Subtitulo,
  confirmarBorrado,
} from "./ui";

/** Miniatura del ícono que subió el seller (la misma imagen que ve el cliente). */
const IconThumb: React.FC<{ url?: string | null; size?: number }> = ({
  url,
  size = 24,
}) =>
  url ? (
    <img
      src={url}
      alt=""
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  ) : null;

const { TextArea } = Input;
const { Text } = Typography;

interface Props {
  sellerCode: string;
}

/**
 * Una opción como se edita: el ajuste y su porcentaje en vez del coeficiente (xcons-docs#636).
 * La API sigue guardando el coeficiente, que multiplica el total (0.9 = 10% de descuento).
 */
type Ajuste = "ninguno" | "descuento" | "recargo";

interface OpcionForm {
  id?: string;
  name: string;
  ajuste: Ajuste;
  porcentaje?: number | null;
}

interface MethodForm extends Omit<OfflinePaymentMethod, "options"> {
  options: OpcionForm[];
}

const AJUSTES = [
  { value: "ninguno", label: "Sin ajuste" },
  { value: "descuento", label: "Descuento" },
  { value: "recargo", label: "Recargo" },
];

const OPCION_VACIA: OpcionForm = { name: "", ajuste: "ninguno" };

const aOpcionForm = (o: OfflineOption): OpcionForm => {
  const c = o.coefficient ?? 1;
  const porcentaje = Math.round(Math.abs(c - 1) * 10000) / 100;
  if (c < 1) return { id: o.id, name: o.name, ajuste: "descuento", porcentaje };
  if (c > 1) return { id: o.id, name: o.name, ajuste: "recargo", porcentaje };
  return { id: o.id, name: o.name, ajuste: "ninguno" };
};

const aOpcion = (o: OpcionForm): OfflineOption => {
  const p = (o.porcentaje ?? 0) / 100;
  const coefficient =
    o.ajuste === "descuento" ? 1 - p : o.ajuste === "recargo" ? 1 + p : 1;
  // Cuatro decimales, los que guarda la API.
  return {
    ...(o.id ? { id: o.id } : {}),
    name: o.name,
    coefficient: Math.round(coefficient * 10000) / 10000,
  };
};

const EMPTY: OfflinePaymentMethod = {
  code: "",
  enabled: true,
  title: "",
  icon: null,
  description: null,
  instructions: null,
  options: [{ name: "", coefficient: 1 }],
};

/** "Efectivo en el local" → "efectivo_en_el_local": el code sugerido a partir del título. */
const slugify = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[^a-z]+/, "")
    .slice(0, 50);

const coefficientLabel = (c: number) => {
  if (c === 1) return "Sin ajuste";
  const pct = Math.round((c - 1) * 10000) / 100;
  return pct < 0 ? `${-pct}% de descuento` : `${pct}% de recargo`;
};

/** El porcentaje de una opción: sin ajuste no se escribe, y un descuento no llega al 100%. */
const PorcentajeOpcion: React.FC<{ name: number }> = ({ name }) => {
  const ajuste: Ajuste | undefined = Form.useWatch(["options", name, "ajuste"]);
  const sinAjuste = !ajuste || ajuste === "ninguno";
  return (
    <Form.Item
      name={[name, "porcentaje"]}
      rules={[{ required: !sinAjuste, message: "%" }]}
    >
      <InputNumber
        disabled={sinAjuste}
        min={0.01}
        max={ajuste === "descuento" ? 99.99 : undefined}
        precision={2}
        suffix="%"
        style={{ width: "100%" }}
      />
    </Form.Item>
  );
};

/**
 * Las opciones de un método en su celda, como los participantes de Presupuestos (xcons-docs#636):
 * cerrada, las que entran en un renglón del ancho de la columna; si sobran, el "+" abre el resto
 * en los renglones de abajo. Cuántas entran lo mide, no lo cuenta: depende del largo de cada una.
 */
const OpcionesEnCelda: React.FC<{
  options: OfflineOption[];
  abierto: boolean;
  onToggle: () => void;
}> = ({ options, abierto, onToggle }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [sobran, setSobran] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // `scrollHeight` cuenta los renglones recortados: si pasa del primero, no entran todas.
    const medir = () => {
      const renglon =
        (el.firstElementChild as HTMLElement | null)?.offsetHeight ?? 0;
      setSobran(el.scrollHeight > renglon + 1);
    };
    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options]);

  return (
    <Flex justify="space-between" align="start" gap={8}>
      <div
        ref={ref}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          minWidth: 0,
          // Cerrada, un solo renglón: el alto de un Tag.
          maxHeight: abierto ? undefined : 22,
          overflow: "hidden",
        }}
      >
        {options.map((o) => (
          <Tag key={o.id ?? o.name} style={{ margin: 0 }}>
            {o.name} · {coefficientLabel(o.coefficient)}
          </Tag>
        ))}
      </div>
      {(sobran || abierto) && (
        <Button
          shape="round"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          icon={abierto ? "-" : "+"}
          iconPosition="end"
        />
      )}
    </Flex>
  );
};

/**
 * ABM de métodos de pago offline del seller (#635): transferencia, efectivo, cheque y los que
 * el seller quiera crear. Cada uno con sus opciones, y cada opción con un descuento o recargo.
 *
 * Sin Card, como Atributos y Productos: la tabla llega a los bordes, "Nuevo método" va en su
 * barra y la fila se abre con un clic (xcons-docs#636).
 */
export const OfflinePaymentMethods: React.FC<Props> = ({ sellerCode }) => {
  const [methods, setMethods] = useState<OfflinePaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  /** `null` = cerrado; un método sin `code` = uno nuevo. */
  const [editing, setEditing] = useState<OfflinePaymentMethod | null>(null);
  /** Los métodos con todas sus opciones a la vista. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (code: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const isNew = !editing?.code;

  const load = async () => {
    setLoading(true);
    try {
      setMethods(await listOfflineMethods(sellerCode));
    } catch {
      message.error("No se pudieron cargar los métodos offline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sellerCode) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const open = (method: OfflinePaymentMethod | null) =>
    setEditing(method ?? EMPTY);

  const close = () => setEditing(null);

  const toggleEnabled = async (m: OfflinePaymentMethod, enabled: boolean) => {
    try {
      const { code, iconUrl: _url, ...rest } = m;
      await updateOfflineMethod(sellerCode, code, { ...rest, enabled });
      setMethods((prev) =>
        prev.map((x) => (x.code === code ? { ...x, enabled } : x)),
      );
    } catch (err: any) {
      message.error(getErrorMessage(err));
    }
  };

  return (
    <>
      <Flex justify="flex-end" style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => open(null)}
        >
          Nuevo método
        </Button>
      </Flex>
      <Table<OfflinePaymentMethod>
        rowKey="code"
        loading={loading}
        dataSource={methods}
        pagination={PAGINADOR}
        className="tabla-con-borde-redondeado"
        bordered
        size="small"
        // Ancho fijo: si no, la columna de Opciones se estira hasta que entren todas en un
        // renglón y el "+" no aparece nunca.
        tableLayout="fixed"
        components={ENCABEZADO_DE_TABLA}
        locale={{ emptyText: "Todavía no hay métodos offline" }}
        onRow={(m) => ({
          style: { cursor: "pointer" },
          onClick: () => open(m),
        })}
        columns={[
          {
            title: "Título",
            dataIndex: "title",
            width: 260,
            render: (_, m) => (
              <Space>
                <IconThumb url={m.iconUrl} />
                <span>{m.title}</span>
              </Space>
            ),
          },
          {
            title: "Código",
            dataIndex: "code",
            width: 200,
            render: (code) => <Text code>{code}</Text>,
          },
          {
            title: "Opciones",
            dataIndex: "options",
            render: (_, m) =>
              m.options.length ? (
                <OpcionesEnCelda
                  options={m.options}
                  abierto={expanded.has(m.code)}
                  onToggle={() => toggleExpanded(m.code)}
                />
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: "Estado",
            dataIndex: "enabled",
            width: 90,
            render: (_, m) => (
              <Switch
                checked={m.enabled}
                onChange={(v) => toggleEnabled(m, v)}
                onClick={(_, e) => e.stopPropagation()}
                size="small"
              />
            ),
          },
        ]}
      />

      {/* El panel lateral estándar, con Cancelar y Guardar en el pie (xcons-docs#636). */}
      <LateralModal
        hideTrigger
        openDrawer={!!editing}
        closeDrawer={close}
        title={
          isNew ? "Nuevo método offline" : `Editar ${editing?.title ?? ""}`
        }
      >
        <MethodPanelBody
          sellerCode={sellerCode}
          method={editing ?? EMPTY}
          isNew={isNew}
          onClose={close}
          onSaved={load}
        />
      </LateralModal>
    </>
  );
};

/**
 * El formulario de un método en el panel lateral. Se monta cada vez que se abre el panel
 * (`LateralModal` lo destruye al cerrar), así que arranca siempre de lo guardado y no arrastra
 * lo que quedó del método anterior (xcons-docs#636).
 */
const MethodPanelBody: React.FC<{
  sellerCode: string;
  method: OfflinePaymentMethod;
  isNew: boolean;
  onClose: () => void;
  onSaved: () => void;
  closeModal?: () => void;
}> = ({ sellerCode, method, isNew, onClose, onSaved }) => {
  const [form] = Form.useForm<MethodForm>();
  const [iconUrl, setIconUrl] = useState<string | null>(method.iconUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codeTouched, setCodeTouched] = useState(!isNew);

  const initialValues: MethodForm = {
    ...method,
    options: method.options.length
      ? method.options.map(aOpcionForm)
      : [OPCION_VACIA],
  };

  const handleDelete = () =>
    confirmarBorrado(
      `¿Eliminar "${method.title}"?`,
      async () => {
        try {
          await deleteOfflineMethod(sellerCode, method.code);
          message.success("Método eliminado");
          onClose();
          onSaved();
        } catch (err: any) {
          message.error(getErrorMessage(err));
        }
      },
      "El cliente deja de verlo. Los pedidos hechos con él no cambian.",
    );

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        icon: form.getFieldValue("icon") ?? null,
        options: (values.options ?? [])
          .filter((o) => o?.name?.trim())
          .map(aOpcion),
      };
      if (isNew) {
        await createOfflineMethod(sellerCode, payload);
        message.success("Método creado");
      } else {
        const { code, iconUrl: _url, ...rest } = payload;
        await updateOfflineMethod(sellerCode, method.code, rest);
        message.success("Método guardado");
      }
      onClose();
      onSaved();
    } catch (err: any) {
      if (err?.errorFields) return; // validación del form: ya se ve en los campos
      message.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const guardar = (
    <Button
      type="primary"
      icon={<CheckOutlined />}
      iconPosition="end"
      loading={saving}
      onClick={handleSave}
    >
      Guardar
    </Button>
  );

  return (
    <CuerpoConPie
      pie={
        // Sin columna de acciones en el listado: borrar es desde el método, a la izquierda del
        // pie, como Eliminar en el panel de plantillas de Canales (xcons-docs#636).
        isNew ? (
          <>
            <Button onClick={onClose}>Cancelar</Button>
            {guardar}
          </>
        ) : (
          <>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Eliminar
            </Button>
            <Space>
              <Button onClick={onClose}>Cancelar</Button>
              {guardar}
            </Space>
          </>
        )
      }
    >
      <Form
        form={form}
        layout="vertical"
        disabled={saving}
        initialValues={initialValues}
        onValuesChange={(changed) => {
          if (isNew && !codeTouched && "title" in changed) {
            form.setFieldsValue({ code: slugify(changed.title ?? "") });
          }
        }}
      >
        <Row gutter={16}>
          <Col flex="auto">
            <Form.Item
              label="Título"
              name="title"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "El título es obligatorio",
                },
              ]}
            >
              <Input placeholder="Ej. Efectivo en el local" />
            </Form.Item>
          </Col>
          <Col flex="none">
            <Form.Item label="Estado" name="enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Código"
          name="code"
          extra="Identifica al método en los pedidos. No se puede cambiar después de crearlo."
          rules={[
            { required: true, message: "El código es obligatorio" },
            {
              pattern: /^[a-z][a-z0-9_]{1,49}$/,
              message: "Minúsculas, números y _ ; empieza con letra",
            },
          ]}
        >
          <Input disabled={!isNew} onChange={() => setCodeTouched(true)} />
        </Form.Item>

        <Form.Item
          label="Ícono"
          extra="La imagen que ve el cliente al lado del método: PNG, SVG o WEBP, idealmente cuadrada. Sin elegir se ve en gris; elegido, en color."
        >
          <Form.Item name="icon" noStyle>
            <Input type="hidden" />
          </Form.Item>
          {/* Como el resto de los archivos del admin: con la imagen subida, el hover muestra el
              ojo para verla grande y el tachito para quitarla (xcons-docs#636). */}
          <Upload
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            listType="picture-card"
            maxCount={1}
            fileList={
              iconUrl
                ? [
                    {
                      uid: "icono",
                      name: "Ícono",
                      status: "done",
                      url: iconUrl,
                    },
                  ]
                : []
            }
            onPreview={() => setPreviewOpen(true)}
            onRemove={() => {
              form.setFieldValue("icon", null);
              setIconUrl(null);
            }}
            customRequest={async ({ file, onSuccess, onError }) => {
              setUploading(true);
              try {
                const res = await uploadOfflineIcon(sellerCode, file as Blob);
                form.setFieldValue("icon", res.path);
                setIconUrl(res.url ?? null);
                onSuccess?.(res);
              } catch (err: any) {
                message.error(
                  `No se pudo subir la imagen: ${getErrorMessage(err)}`,
                );
                onError?.(err);
              } finally {
                setUploading(false);
              }
            }}
          >
            {!iconUrl && (
              <div>
                {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                <div style={{ marginTop: 4 }}>Subir</div>
              </div>
            )}
          </Upload>
          {iconUrl && (
            <Image
              src={iconUrl}
              style={{ display: "none" }}
              preview={{
                visible: previewOpen,
                onVisibleChange: setPreviewOpen,
              }}
            />
          )}
        </Form.Item>

        <Form.Item label="Descripción" name="description">
          <Input placeholder="Texto corto debajo del título" />
        </Form.Item>

        <Form.Item label="Instrucciones para el cliente" name="instructions">
          <TextArea
            rows={4}
            placeholder="Lo que ve el cliente al elegirlo y en el mail del pedido"
          />
        </Form.Item>

        <Subtitulo>Opciones</Subtitulo>
        <Form.List name="options">
          {(fields, { add, remove }) => (
            <>
              {fields.length > 0 && (
                <Row gutter={8} wrap={false} style={{ marginBottom: 8 }}>
                  <Col flex="auto" style={{ minWidth: 0 }}>
                    <Text type="secondary">Nombre</Text>
                  </Col>
                  <Col flex="120px">
                    <Text type="secondary">Ajuste</Text>
                  </Col>
                  <Col flex="96px">
                    <Text type="secondary">Porcentaje</Text>
                  </Col>
                  <Col flex="32px" />
                </Row>
              )}
              {fields.map(({ key, name }) => (
                <Row key={key} gutter={8} align="top" wrap={false}>
                  <Form.Item name={[name, "id"]} hidden>
                    <Input />
                  </Form.Item>
                  <Col flex="auto" style={{ minWidth: 0 }}>
                    <Form.Item
                      name={[name, "name"]}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Nombre de la opción",
                        },
                      ]}
                    >
                      <Input placeholder="Ej. Contado" />
                    </Form.Item>
                  </Col>
                  <Col flex="120px">
                    <Form.Item name={[name, "ajuste"]}>
                      <Select
                        options={AJUSTES}
                        onChange={(v) => {
                          if (v === "ninguno") {
                            form.setFieldValue(
                              ["options", name, "porcentaje"],
                              null,
                            );
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="96px">
                    <PorcentajeOpcion name={name} />
                  </Col>
                  <Col flex="32px">
                    <Tooltip title="Quitar opción">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        aria-label="Quitar opción"
                      />
                    </Tooltip>
                  </Col>
                </Row>
              ))}
              {/* La fila para agregar, como en Datos adicionales del contacto: los campos
                  apagados y el "+" a la derecha (xcons-docs#636). */}
              <Row gutter={8} align="top" wrap={false}>
                <Col flex="auto" style={{ minWidth: 0 }}>
                  <Input placeholder="Nombre" disabled />
                </Col>
                <Col flex="120px">
                  <Select
                    placeholder="Ajuste"
                    disabled
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col flex="96px">
                  <InputNumber
                    placeholder="0"
                    suffix="%"
                    disabled
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col flex="32px">
                  <Tooltip title="Agregar opción">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => add(OPCION_VACIA)}
                      aria-label="Agregar opción"
                    />
                  </Tooltip>
                </Col>
              </Row>
            </>
          )}
        </Form.List>
      </Form>
    </CuerpoConPie>
  );
};
